import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  panel: {
    width: '360px',
    minWidth: '320px',
    maxHeight: '800px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxHeight: 'none',
    },
  },
  header: {
    padding: theme.spacing(2, 3),
  },
  body: {
    overflowY: 'auto',
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  helperText: {
    color: theme.palette.text.secondary,
  },
  emptyState: {
    padding: theme.spacing(3),
  },
  loadingState: {
    padding: theme.spacing(4, 3),
    display: 'flex',
    justifyContent: 'center',
  },
}));

const formatAvailabilityLabel = (session, t) => {
  const { startDate, endDate, availabilityStatus } = session;

  if (availabilityStatus === 'upcoming' && startDate) {
    return t('trainingSessionSchedule.availableFromLabel', {
      date: moment(startDate).format('DD.MM.YYYY'),
    });
  }

  if (availabilityStatus === 'completed' && endDate) {
    return t('trainingSessionSchedule.availableUntilLabel', {
      date: moment(endDate).format('DD.MM.YYYY'),
    });
  }

  if (startDate && endDate) {
    return t('trainingSessionSchedule.availableRange', {
      start: moment(startDate).format('DD.MM.YYYY'),
      end: moment(endDate).format('DD.MM.YYYY'),
    });
  }

  if (startDate) {
    return t('trainingSessionSchedule.availableFromLabel', {
      date: moment(startDate).format('DD.MM.YYYY'),
    });
  }

  if (endDate) {
    return t('trainingSessionSchedule.availableUntilLabel', {
      date: moment(endDate).format('DD.MM.YYYY'),
    });
  }

  return t('trainingSessionSchedule.availableAnytime');
};

const FlexibleSessionsPanel = ({ sessions, onSelectSession, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  return (
    <Paper className={classes.panel} elevation={1}>
      <Box className={classes.header}>
        <Typography variant="h6">{t('trainingSessionSchedule.flexibleSessions')}</Typography>
      </Box>
      <Divider />
      <div className={classes.body}>
        {loading && !sessions.length ? (
          <Box className={classes.loadingState}>
            <CircularProgress size={24} />
          </Box>
        ) : !sessions.length ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2" className={classes.helperText}>
              {t('trainingSessionSchedule.flexibleEmpty')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {sessions.map(session => {
              const title = [session.trainingModuleName || session.trainingModuleCode, session.trainingPartCode]
                .filter(Boolean)
                .join(' · ');

              return (
                <React.Fragment key={session.id}>
                  <ListItem button alignItems="flex-start" onClick={() => onSelectSession(session)}>
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box display="flex" flexDirection="column">
                          <Typography variant="subtitle1">{title}</Typography>
                          <Box mt={1}>
                            <Chip
                              className={classes.chip}
                              label={t(`trainingSessions.deliveryMethods.${session.deliveryMethod}`)}
                              color="primary"
                              size="small"
                            />
                            {session.stationName && (
                              <Chip
                                className={classes.chip}
                                label={session.stationName}
                                variant="outlined"
                                size="small"
                              />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <>
                          <Box mt={1}>
                            <Typography variant="body2" color="textSecondary">
                              {formatAvailabilityLabel(session, t)}
                            </Typography>
                          </Box>
                          {session.trainerName && (
                            <Box mt={1}>
                              <Typography variant="body2" color="textSecondary">
                                {t('schedule.trainers')}
                                {': '}
                                {session.trainerName}
                              </Typography>
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </div>
    </Paper>
  );
};

FlexibleSessionsPanel.propTypes = {
  sessions: PropTypes.arrayOf(PropTypes.shape({})),
  onSelectSession: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

FlexibleSessionsPanel.defaultProps = {
  sessions: [],
  loading: false,
};

export default FlexibleSessionsPanel;
